<template>
  <div>
    <component
      :is="targetComponent"
      v-model="_value"
      v-on="$listeners"
      v-bind="targetComponentProps"
      :name="name"
      ref="editor"
      :mentions="macroIntellisense"
      @initialized="onInitiailized"
      @customize-modules="customizeQuillModules"
      @blur="runSpellCheck"
    />
    <div class="d-flex justify-content-end status-overlay-row">
      <IconButton
        :class="'spell-check-status pb-0 ' + spellCheckStatusClass"
        icon="circle"
        @click="handleClickSpellCheckIcon"
        v-tooltip="iconHoverText"
      />
    </div>
    <div
      class="spell-check-box"
      ref="spellCheckBox"
      v-if="selectedWordToCheck"
      v-bind="spellCheckBoxPosition"
    >
      <SpellCheckBox
        :wordToCheck="wordToCheck"
        @close="closeSpellCheck"
        @ignore="ignoreWord"
        @ignoreAll="ignoreAll"
        @replace="handleReplaceWord"
        @addWord="handleAddWord"
        @replaceAll="handleReplaceAll"
        @move="setSpellCheckBoxPosition"
        :isForcedUpperCase="isForcedUpperCase"
      />
    </div>
    <Modal :status="isDictionaryMaintenanceOpen" @close="toggleDictionaryMaintenance">
      <EditUserDictionary :userId="currentUser.id" @close="toggleDictionaryMaintenance" />
    </Modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import MacroModule, { macroStore } from "@/modules/Quill/MacroIntellisense.js";
import { MacroTypeEnum } from "@/modules/enums";
import Macro from "@/modules/Quill/Macro";
import Quill from "devextreme-quill";
import { getDefaultStyles } from "@/modules/getDefaultStylesByField";
import SpellCheckBox from "../SpellCheckBox.vue";
import spellChecker from "@/mixins/spellChecker";
import { getTextFromHtml } from "@/modules/helpers";
import IconButton from "./IconButton.vue";
import EditUserDictionary from "../EditUserDictionary.vue";
import Modal from "./Modal.vue";

Quill.register({ "modules/macro": Macro });

export const regularExpressions = {
  group: /\.\.([rgpsmd|\s])$/i,
  single: /([.|\\])([\w\-+]+(;[dmsc])?)/i,
  decimalNumber: /[0-9]+[.]([0-9]+)/i,
  specimen: /((?:([.|\\])([\w\-+]+(;[dmsc])?))+),([\w*>=<]+)/i,
  specimenWithBlock: /(?:([.|\\])([\w\-+]+(;[dmsc])?))+,([\w*>=<]+)(,[\w])/i,
  block: /((?:[.][\w+-]+)+),(=?[\w*><=]{1,3}),([\w*><=]+)/i,
  singleWithoutDot: /^([\w\-+]+)(([.|\\])([\w\-+]+(;[dmsc])?))+/i,
  specimenWithoutDot: /((^([\w\-+]+(;[dmsc])?))+),([\w*>=<]+)/i
};

export default {
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: String,
      default: ""
    },
    expanded: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Editor: () => import("@/components/common/Editor.vue"),
    DragonEditor: () => import("@/components/common/DragonEditor.vue"),
    SpellCheckBox,
    IconButton,
    EditUserDictionary,
    Modal
  },
  mixins: [spellChecker],
  data: () => ({
    component: {},
    _editor: null,
    isSpellCheckRunning: false,
    intervalId: null,
    spellCheckIteration: 0,
    editorCheckerStatus: false,
    isDictionaryMaintenanceOpen: false,
    problemWords: []
  }),
  mounted() {
    if (this.expanded) {
      this.expand();
    }
    window.addEventListener("keydown", this.handleKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyDown);
  },
  watch: {
    useDragonEditors: {
      handler() {
        const { isExpanded, isFocused } = this.$refs.editor;
        if (isExpanded) {
          setTimeout(() => {
            this.expand();
            if (isFocused) {
              this.$refs.editor.focus();
            }
          }, 500);
        }
      }
    }
  },
  methods: {
    onInitiailized({ component }) {
      if (component) {
        component.register({ "modules/mentions": MacroModule }, true);
      }
      this.component = component;
      this.$emit("editorReady", this.name);
    },
    focus() {
      if (this.$refs?.editor?.focus) {
        this.$refs.editor.focus();
      }
    },
    expand() {
      if (this.$refs?.editor?.expand) {
        this.$refs.editor.expand();
      }
    },
    collapse() {
      if (this.$refs?.editor) {
        this.$refs.editor.collapse();
      }
    },
    customizeQuillModules(config) {
      config.macro = {
        name: this.name,
        styles: this.styles,
        initialized: true
      };
    },
    async handleClickSpellCheckIcon() {
      if (!this.enableSpellchecker) {
        const confirm = await window.confirm(
          "Spell check is not enabled. Would you like to enable it?"
        );
        if (confirm) {
          this.$store.dispatch("applicationSettings/setUserSettings", { enableSpellchecker: true });
        }
      } else if (this.problemWords?.length) {
        this.runSpellCheckOnSave();
      } else {
        this.toggleDictionaryMaintenance();
      }
    },
    toggleDictionaryMaintenance() {
      this.isDictionaryMaintenanceOpen = !this.isDictionaryMaintenanceOpen;
    },
    handleKeyDown({ key, altKey }) {
      if (this.$refs.editor.isFocused && altKey && key === "F2") {
        this.handleClickSpellCheckIcon();
      }
    }
  },
  computed: {
    ...mapState({
      applicationSettings: state => state.applicationSettings,
      specimens: state => state.accessionStore.specimens,
      MacroSearchWithPeriod: state => state.labSettings.MacroSearchWithPeriod,
      currentSpecimen: state => state.accessionStore.currentSpecimen,
      SpellCheckOnSave: state => state.labSettings.SpellCheckOnSave,
      useDragonEditors: state => state.applicationSettings.useDragonEditors,
      labSettings: state => state.labSettings,
      enableSpellchecker: state => state.applicationSettings.enableSpellchecker,
      currentUser: state => state.currentUser
    }),
    ...mapGetters(["permissions"]),
    webSpellCheckInstance() {
      if (this.$refs.editor) {
        return this.$refs.editor.webSpellCheckInstance;
      }
      return null;
    },
    targetComponent() {
      return this.useDragonEditors ? "DragonEditor" : "Editor";
    },
    targetComponentProps() {
      return this.useDragonEditors
        ? { ...this.$attrs, isSpellCheckRunning: this.isSpellCheckRunning }
        : { ...this.$attrs, mentions: this.macroIntellisense, onInitiailized: this.onInitiailized };
    },
    targetList() {
      return [...(this.specimens.map(e => e.specimenOrder) || []), "*", ">", "<", ">=", "<="];
    },
    _value: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    styles() {
      return getDefaultStyles(this.name, true);
    },
    macroIntellisense() {
      if (this.applicationSettings.macroAssist) {
        return [
          {
            displayExpr: "displayName",
            searchExpr: "displayName",
            valueExpr: "displayName",
            dataSource: macroStore(),
            marker: ".",
            minSearchLength: 2,
            itemTemplate: ({ displayName, macroType }) => {
              if (macroType === MacroTypeEnum.Results) {
                return `<strong>${displayName}</strong>`;
              }
              return displayName;
            }
          },
          {
            displayExpr: "displayName",
            searchExpr: "displayName",
            valueExpr: "displayName",
            dataSource: macroStore(),
            marker: "\\",
            minSearchLength: 2,
            itemTemplate: ({ displayName, macroType }) => {
              if (macroType === MacroTypeEnum.Results) {
                return `<strong>${displayName}</strong>`;
              }
              return displayName;
            }
          },
          {
            dataSource: this.targetList,
            marker: ",",
            minSearchLength: 0,
            isExtension: true
          },
          {
            dataSource: ["C", "D", "M", "S"],
            marker: ";",
            minSearchLength: 0,
            isExtension: true
          }
        ];
      }
      return [];
    },
    spellCheckStatusClass() {
      if (!this.enableSpellchecker) {
        return "text-muted";
      }
      if (!getTextFromHtml(this.value)?.length) {
        return "text-primary";
      }
      if (this.isSpellCheckRunning) {
        return "text-warning";
      }
      if (this.problemWords?.length) {
        return "text-danger";
      }
      return "text-success";
    },
    iconHoverText() {
      if (!this.enableSpellchecker) {
        return "Spell checker is not enabled<br>Click to enable";
      }
      if (!getTextFromHtml(this.value)?.length) {
        return "No text";
      }
      if (this.isSpellCheckRunning) {
        return "Spell check running...";
      }
      if (this.problemWords?.length) {
        return `${this.problemWords.length} spelling error${
          this.problemWords.length > 1 ? "s" : ""
        } found`;
      }
      return "No spelling errors found";
    }
  }
};
</script>
<style lang="scss" scoped>
.toolbar {
  width: 100%;
}
.title {
  text-transform: capitalize;
  font-size: 1.2rem;
}
.is-invalid {
  border-color: #dc3545 !important;
}
.is-valid {
  border-color: #28a745 !important;
}

::v-deep .dx-icon,
::v-deep .dx-dropdowneditor-icon {
  &::before {
    color: #333 !important;
  }
}

.spell-check-box {
  position: absolute;
  top: 500px;
  left: 600px;
  display: flex;
}
.spell-check-status {
  position: relative;
  display: flex;
  font-size: 0.5 em;
  transform: translateY(-2.25em);
  padding-right: 1.5em;
}
.status-overlay-row {
  height: 0;
}
</style>
